/* eslint-disable react/no-unescaped-entities */
import React from "react";
import LegalLayout, { H2, H3, P, Ul, ServiceCard } from "./LegalLayout";
import Seo from "../Seo";

const CONTROLLER_EMAIL = "privacy@mydigitalaction.it";
const CONTROLLER_ADDRESS = "Via San Franceschino 69/D, 55047 Seravezza (LU), Italia";
const CONTROLLER_VAT = "02124640461";

export default function PrivacyPolicyPage() {
  return (
    <LegalLayout title="Privacy Policy" kicker="Trattamento dei dati personali" lastUpdated="Febbraio 2026">
      <Seo title="Privacy Policy — MyDigitalAction" description="Informativa sul trattamento dei dati personali del sito mydigitalaction.it ai sensi del GDPR." path="/privacy-policy" />
      <P>
        La presente informativa descrive le modalità con cui i Dati Personali degli Utenti vengono raccolti e trattati
        tramite il sito <strong className="text-white">mydigitalaction.it</strong> (di seguito &laquo;Sito&raquo; o
        &laquo;Applicazione&raquo;), in conformità al Regolamento (UE) 2016/679 (&laquo;GDPR&raquo;) e alla normativa
        italiana vigente in materia di protezione dei dati personali.
      </P>

      <H2>Titolare del trattamento</H2>
      <P>
        Digital Formula di Francesco Gherardi &mdash; MyDigitalAction Experience<br />
        Sede legale: {CONTROLLER_ADDRESS}<br />
        Partita IVA: {CONTROLLER_VAT}<br />
        Email del Titolare: <a href={`mailto:${CONTROLLER_EMAIL}`} style={{ color: "#d4ff3d" }}>{CONTROLLER_EMAIL}</a>
      </P>

      <H2>Tipologie di dati raccolti</H2>
      <P>
        Fra i Dati Personali raccolti da questa Applicazione, in modo autonomo o tramite terze parti, ci sono:
      </P>
      <Ul>
        <li>Nome, cognome, indirizzo email e numero di telefono (forniti volontariamente dall'Utente in fase di iscrizione al quiz di orientamento, checkout o richieste dirette).</li>
        <li>Dati di pagamento gestiti direttamente da Stripe (il nostro server non memorizza mai numeri di carta o CVV).</li>
        <li>Risposte al quiz di orientamento e preferenze marketing espresse dall'Utente.</li>
        <li>Contenuti dei messaggi WhatsApp o email inviati al supporto.</li>
        <li>Dati di utilizzo (indirizzo IP, tipo di browser, sistema operativo, pagine visitate, orari e durata delle sessioni) raccolti automaticamente.</li>
        <li>Strumenti di tracciamento (cookie tecnici e, previo consenso, cookie analitici e di marketing).</li>
      </Ul>
      <P>
        Il conferimento dei dati contrassegnati come obbligatori nei form del Sito &egrave; necessario per fornire il
        Servizio richiesto. Il mancato conferimento potrebbe rendere impossibile l'iscrizione ai corsi o la fruizione
        di alcune funzionalit&agrave;.
      </P>

      <H2>Modalit&agrave; e luogo del trattamento</H2>
      <H3>Modalit&agrave;</H3>
      <P>
        Il Titolare adotta misure di sicurezza tecniche e organizzative adeguate per prevenire l'accesso, la divulgazione,
        la modifica o la distruzione non autorizzati dei Dati Personali. Il trattamento &egrave; effettuato mediante
        strumenti informatici e telematici, con logiche strettamente correlate alle finalit&agrave; indicate.
      </P>
      <H3>Luogo</H3>
      <P>
        I Dati sono trattati principalmente in Italia e in altri paesi dell'Unione Europea. Alcuni servizi terzi
        utilizzati dal Titolare possono comportare il trasferimento di Dati verso paesi extra-UE (in particolare Stati Uniti),
        sempre nel rispetto delle garanzie previste dal GDPR (Standard Contractual Clauses e Data Processing Agreements).
      </P>
      <H3>Periodo di conservazione</H3>
      <Ul>
        <li>Dati raccolti per finalit&agrave; contrattuali (iscrizioni a corsi, pagamenti): conservati per 10 anni ai sensi degli obblighi civilistici e fiscali italiani.</li>
        <li>Dati raccolti per finalit&agrave; di marketing e newsletter: conservati fino alla revoca del consenso da parte dell'Utente.</li>
        <li>Dati raccolti tramite il quiz di orientamento (senza acquisto): conservati per 24 mesi, salvo cancellazione anticipata su richiesta.</li>
      </Ul>

      <H2>Finalit&agrave; del trattamento</H2>
      <P>I Dati dell'Utente sono raccolti per le seguenti finalit&agrave;:</P>
      <Ul>
        <li>Erogazione dei corsi e gestione dell'iscrizione (base giuridica: esecuzione del contratto).</li>
        <li>Gestione dei pagamenti tramite Stripe (base giuridica: esecuzione del contratto).</li>
        <li>Invio di comunicazioni transazionali (conferme ordine, promemoria call di orientamento, risultati quiz) tramite Resend (base giuridica: esecuzione del contratto).</li>
        <li>Gestione delle richieste di supporto via WhatsApp o email (base giuridica: legittimo interesse).</li>
        <li>Prenotazione di call di orientamento tramite Cal.com (base giuridica: consenso / esecuzione di misure precontrattuali).</li>
        <li>Analisi statistica e ottimizzazione del Sito (base giuridica: legittimo interesse, cookie analitici previo consenso).</li>
        <li>Invio di comunicazioni commerciali e newsletter (base giuridica: consenso esplicito dell'Utente, sempre revocabile).</li>
        <li>Adempimento di obblighi legali, contabili e fiscali (base giuridica: obbligo di legge).</li>
      </Ul>

      <H2>Servizi di terze parti utilizzati</H2>
      <P>Di seguito l'elenco dei principali fornitori terzi che partecipano al trattamento dei Dati:</P>

      <ServiceCard
        name="Stripe (Pagamenti)"
        provider="Stripe Payments Europe, Ltd."
        country="Irlanda / UE"
        purposes="Elaborazione dei pagamenti tramite carta, PayPal e altri metodi elettronici."
        dataTypes="Dati di pagamento (elaborati direttamente dal fornitore), nome, cognome, email, indirizzo di fatturazione."
        privacyUrl="https://stripe.com/it/privacy"
      />
      <ServiceCard
        name="Resend (Email transazionali)"
        provider="Resend, Inc."
        country="USA (con garanzie GDPR)"
        purposes="Invio di email di conferma ordine, notifiche transazionali, risultati quiz e password recovery admin."
        dataTypes="Indirizzo email, nome, cognome, contenuto del messaggio."
        privacyUrl="https://resend.com/legal/privacy-policy"
      />
      <ServiceCard
        name="Cal.com (Prenotazione call)"
        provider="Cal.com, Inc."
        country="USA (con garanzie GDPR)"
        purposes="Gestione della prenotazione delle call gratuite di orientamento con i consulenti MyDigitalAction."
        dataTypes="Nome, email, fuso orario, orario prenotato."
        privacyUrl="https://cal.com/privacy"
      />
      <ServiceCard
        name="WhatsApp Business (Assistenza clienti)"
        provider="Meta Platforms Ireland Ltd."
        country="Irlanda / UE"
        purposes="Assistenza clienti tramite chat WhatsApp attivata dal pulsante flottante sul Sito."
        dataTypes="Numero di telefono, nome, cognome, contenuto dei messaggi, orari di invio, immagine profilo."
        privacyUrl="https://www.whatsapp.com/legal/privacy-policy"
      />
      <ServiceCard
        name="MongoDB Atlas (Database)"
        provider="MongoDB, Inc. (via Emergent Labs)"
        country="UE"
        purposes="Archiviazione dei dati degli iscritti, ordini, attivit&agrave; CRM."
        dataTypes="Tutti i dati personali forniti dall'Utente."
        privacyUrl="https://www.mongodb.com/legal/privacy-policy"
      />
      <ServiceCard
        name="Cloudflare (CDN e sicurezza)"
        provider="Cloudflare, Inc."
        country="USA (con garanzie GDPR)"
        purposes="Distribuzione dei contenuti del Sito, protezione DDoS, ottimizzazione delle prestazioni."
        dataTypes="Indirizzo IP, dati di utilizzo, strumenti di tracciamento tecnici."
        privacyUrl="https://www.cloudflare.com/it-it/privacypolicy/"
      />
      <ServiceCard
        name="Google Fonts"
        provider="Google Ireland Ltd."
        country="Irlanda / UE"
        purposes="Caricamento dei font tipografici utilizzati sul Sito."
        dataTypes="Indirizzo IP, dati di utilizzo."
        privacyUrl="https://policies.google.com/privacy"
      />

      <H2>Diritti dell'Utente (Regolamento GDPR)</H2>
      <P>In relazione ai propri Dati Personali, l'Utente ha diritto di:</P>
      <Ul>
        <li><strong className="text-white/90">Accedere</strong> ai propri Dati e ottenerne copia.</li>
        <li><strong className="text-white/90">Rettificare</strong> Dati inesatti o incompleti.</li>
        <li><strong className="text-white/90">Cancellare</strong> i propri Dati (&laquo;diritto all'oblio&raquo;) nei limiti previsti dalla legge.</li>
        <li><strong className="text-white/90">Limitare</strong> il trattamento in determinate circostanze.</li>
        <li><strong className="text-white/90">Opporsi</strong> al trattamento basato su legittimo interesse o marketing diretto.</li>
        <li><strong className="text-white/90">Portabilit&agrave;</strong>: ricevere i propri Dati in formato strutturato e trasferirli ad altro titolare.</li>
        <li><strong className="text-white/90">Revocare il consenso</strong> in qualsiasi momento, senza pregiudicare la liceit&agrave; del trattamento precedente.</li>
        <li><strong className="text-white/90">Proporre reclamo</strong> al Garante per la Protezione dei Dati Personali (www.garanteprivacy.it) o all'autorit&agrave; di controllo competente.</li>
      </Ul>
      <P>
        Per esercitare i propri diritti, l'Utente pu&ograve; contattare il Titolare all'indirizzo{" "}
        <a href={`mailto:${CONTROLLER_EMAIL}`} style={{ color: "#d4ff3d" }}>{CONTROLLER_EMAIL}</a>. Il Titolare
        risponder&agrave; entro 30 giorni dalla richiesta.
      </P>

      <H2>Cookie</H2>
      <P>
        Questa Applicazione utilizza cookie tecnici e, previo consenso dell'Utente, cookie analitici e di marketing. Per
        maggiori informazioni consulta la <a href="/cookie-policy" style={{ color: "#d4ff3d" }}>Cookie Policy</a>.
      </P>

      <H2>Log di sistema</H2>
      <P>
        Per necessit&agrave; legate al funzionamento e alla manutenzione, questa Applicazione e i servizi terzi da essa
        utilizzati possono raccogliere log di sistema, ossia file che registrano le interazioni e che possono contenere
        anche Dati Personali quali l'indirizzo IP dell'Utente.
      </P>

      <H2>Modifiche alla Privacy Policy</H2>
      <P>
        Il Titolare si riserva il diritto di apportare modifiche alla presente informativa in qualsiasi momento,
        notificandole agli Utenti su questa pagina. Si consiglia di consultare periodicamente questa pagina, facendo
        riferimento alla data di ultimo aggiornamento riportata in cima.
      </P>
    </LegalLayout>
  );
}
